<template>
  <DataTable
    :value="adsCabinets"
    :class="[
      adsCabinets && adsCabinets.length == 0 ? 'empty-table' : '',
      isActiveFirst ? 'active-first' : '',
    ]"
    class="scroll-table-y main-table scrollbar-custom"
    stripedRows
    @sort="sortTable"
    removableSort
    scrollable
    :loading="isLoading"
    dataKey="id"
    columnResizeMode="expand"
  >
    <Column field="number" header="№" style="min-width: 44px">
      <template #body="slotProps">
        {{ rowsCount + slotProps.index + 1 }}
      </template>
    </Column>
    <Column field="name" header="Название кабинета" sortable>
      <template #body="slotProps">
        <RouterLink
          :to="{ name: 'adsCabinet', params: { id: slotProps.data.id } }"
        >
          {{ slotProps.data.name }}
        </RouterLink>
      </template>
    </Column>
    <Column field="source" header="Площадка">
      <template #body="slotProps">
        {{ slotProps.data.source.name }}
      </template>
    </Column>
    <Column field="login" header="Логин" sortable> </Column>
    <Column field="external_id" header="Внешний id" sortable>
      <template #body="slotProps">
        {{ slotProps.data.external_id || '-' }}
      </template>
    </Column>
    <Column field="cuser" header="Автор" sortable>
      <template #body="slotProps">
        {{ getUserFullName(slotProps.data.cuser) }}
      </template>
    </Column>
    <Column field="is_stat" header="Сбор статистики в SSP">
      <template #body="slotProps">
        <span v-if="slotProps.data.is_statistics_required">Да</span>
        <span v-else>Нет</span>
      </template>
    </Column>
    <Column field="is_stat" header="Проверка имени в SSP">
      <template #body="slotProps">
        <span v-if="slotProps.data.is_naming_checked">Да</span>
        <span v-else>Нет</span>
      </template>
    </Column>
    <Column field="use_in_ui" header="Использовать в UI">
      <template #body="slotProps">
        <span v-if="slotProps.data.use_in_ui">Да</span>
        <span v-else>Нет</span>
      </template>
    </Column>
    <Column field="settings" header="Settings" style="max-width: 200px">
      <template #body="slotProps">
        <JsonWithTooltip :value="slotProps.data.settings" :max-length="50" />
      </template>
    </Column>
    <Column
      field="ctime"
      header="Дата создания"
      sortable
      style="min-width: 153px"
    >
      <template #body="slotProps">
        {{ convertDateFromUTC(slotProps.data.ctime) }}
      </template>
    </Column>
    <Column
      field="mtime"
      header="Дата обновления"
      sortable
      style="min-width: 173px; max-width: 173px"
    >
      <template #body="slotProps">
        {{ convertDateFromUTC(slotProps.data.mtime) }}
      </template>
    </Column>
    <Column frozen alignFrozen="right">
      <template #body="slotProps">
        <div
          @click="(event) => toggle(slotProps.data.id, event)"
          aria-haspopup="true"
          aria-controls="overlay_menu"
          class="button-menu"
        >
          <span :data-segment="1" class="pi pi-ellipsis-v"></span>
        </div>
      </template>
    </Column>
  </DataTable>
  <Menu ref="menu" id="overlay_menu" :model="filteredItems" :popup="true" />
  <DeleteConfirmDialog
    deleted-name="рекламный кабинет"
    :visible="confirmVisible.delete"
    :blocked="blockedButtonDelete"
    @close="confirmVisible.delete = false"
    @delete="deleteAdsCabinet"
  />
  <UsersHistoryDialog
    entity="ads_cabinets"
    :object_id="modifiedId"
    :visible="confirmVisible.history"
    @close="confirmVisible.history = false"
  ></UsersHistoryDialog>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/store/auth/login";
import { useAdsCabinetsStore } from "@/store/dictionaries/ads_cabinets";

import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Menu from "primevue/menu";
import UsersHistoryDialog from "@/widgets/dialogs/UsersHistoryDialog.vue";
import DeleteConfirmDialog from "@/shared/dialogs/DeleteConfirmDialog.vue";
import JsonWithTooltip from "@/shared/table/JsonWithTooltip.vue";

import { convertDateFromUTC } from "@/services/utils/DateParser";
import { getUserFullName } from "@/services/utils/Names";
import { rootGetters } from "@/main";

defineProps<{
  adsCabinets: any[];
  isActiveFirst: boolean;
  isLoading: boolean;
  rowsCount: number;
}>();
const emits = defineEmits(["sortTable", "updateTable"]);
const router = useRouter();
const store = useAdsCabinetsStore();
const authStore = useAuthStore();

const isDenied = computed(() => {
  return authStore.isDeniedForPermissions(["history:edit"], ["history:read"]);
});

let modifiedId = ref(0);
const confirmVisible = ref({
  history: false,
  delete: false,
});
const blockedButtonDelete = ref(false);
const menu = ref();
const items = ref([
  {
    label: "История действий",
    icon: "pi pi-history",
    checkDenied: true,
    archive: "both",
    command: () => {
      confirmVisible.value.history = true;
    },
  },
  {
    label: "Редактировать",
    icon: "pi pi-pencil",
    archive: "no_archive",
    command: () => {
      router.push({ name: "adsCabinet", params: { id: modifiedId.value } });
    },
  },
  {
    label: "Удалить",
    icon: "pi pi-trash",
    archive: "both",
    command: () => {
      confirmVisible.value.delete = true;
    },
  },
]);

const filteredItems = computed(() => {
  return items.value.filter((item) => !(item.checkDenied && isDenied.value));
});

const toggle = (elemId: number, event: any) => {
  modifiedId.value = elemId;
  menu.value.toggle(event);
};

const sortTable = (event: any) => {
  let orderField = "";
  if (event.sortField)
    orderField = event.sortOrder > 0 ? event.sortField : `-${event.sortField}`;
  emits("sortTable", orderField);
};

const deleteAdsCabinet = () => {
  blockedButtonDelete.value = true;
  store
    .deleteAdsCabinet(modifiedId.value)
    .finally(() => {
      emits("updateTable");
      blockedButtonDelete.value = false;
      confirmVisible.value.delete = false;
    })
    .catch((err) => {
      if (err.response && err.response.status == 400)
        rootGetters.toast.show("warn", "Ошибка", err.response.data.detail);
    });
};
</script>

